import type { EntityHooks } from "@/components/shared/UniversalCRMTable/types/shared";
import type { SimpleFieldConfig } from "@/components/shared/UniversalCRMTable/types";
import type { RecurringCharge, RecurringChargeFilters } from "@/lib/types/recurring-charges";
import {
  useRecurringCharges,
  useRecurringCharge,
  useCreateRecurringCharge,
  useUpdateRecurringCharge,
  useDeleteRecurringCharge,
} from "./recurring-charges-queries";

const RECURRING_CHARGE_FIELD_CONFIGS: Record<string, SimpleFieldConfig> = {
  id: { type: "text", label: "ID" },
  customer_id: { type: "text", label: "Customer ID" },
  schedule_type: { type: "select", label: "Schedule Type" },
  last_charged_at: { type: "date", label: "Last Charged" },
  next_charge_date: { type: "date", label: "Next Charge Date" },
  metadata: { type: "json", label: "Charge Details" },
  realm_id: { type: "text", label: "Realm ID" },
  admin_email: { type: "text", label: "Admin Email" },
  created_at: { type: "date", label: "Created" },
  updated_at: { type: "date", label: "Updated" },
};

export function useRecurringChargeHooksForPage(): EntityHooks<RecurringCharge> {
  return {
    usePaginated: (params?: RecurringChargeFilters) => {
      const { data, loading, error, refetch } = useRecurringCharges(params);
      return {
        data: data ? { data, totalCount: 0, pageInfo: undefined } : undefined,
        isLoading: loading,
        error: error as Error | null,
        refetch: refetch || (() => {}),
      };
    },
    useInfinite: (params?: RecurringChargeFilters) => {
      const { data, loading, error, refetch } = useRecurringCharges(params);
      return {
        data: data ? { pages: [{ data, totalCount: 0, hasNextPage: false }] } : undefined,
        isLoading: loading,
        error: error as Error | null,
        refetch: refetch || (() => {}),
        hasNextPage: false,
        isFetchingNextPage: false,
        fetchNextPage: () => {},
      };
    },
    useSingle: (id: string) => {
      const { data, loading, error, refetch } = useRecurringCharge(id);
      return {
        data: data || undefined,
        isLoading: loading,
        error: error as Error | null,
        refetch: refetch || (() => {}),
      };
    },
    useCreate: () => {
      const { mutateAsync, isPending } = useCreateRecurringCharge();
      return {
        mutateAsync: async (data: Partial<RecurringCharge> & { id?: string }) => {
          if (
            !data.customer_id ||
            !data.schedule_type ||
            !data.next_charge_date ||
            !data.realm_id ||
            !data.admin_email
          ) {
            throw new Error("Missing required recurring charge fields");
          }

          return mutateAsync({
            customer_id: data.customer_id,
            schedule_type: data.schedule_type,
            next_charge_date: data.next_charge_date,
            metadata: data.metadata,
            realm_id: data.realm_id,
            admin_email: data.admin_email,
            last_charged_at: data.last_charged_at,
          });
        },
        isPending,
      };
    },
    useUpdate: () => {
      const { mutateAsync, isPending } = useUpdateRecurringCharge();
      return {
        mutateAsync: async (data: Partial<RecurringCharge> & { id: string }) => {
          const { id, ...updates } = data;
          return mutateAsync({ id, ...updates });
        },
        isPending,
      };
    },
    useDelete: useDeleteRecurringCharge,
    getFieldConfig: (fieldName: string) => RECURRING_CHARGE_FIELD_CONFIGS[fieldName],
  };
}
